import vmClient from 'app/services/vmClient';
import { Entity, EntityStringId, IEntity, IEntityType } from 'app/types/entity';
import { IBrandLogo, BrandLogo } from 'app/constants/schemas/BrandLogo';
import { ICurriculum, Curriculum } from 'app/constants/schemas/Curriculum';
import { IText, Text } from 'app/constants/schemas/Text';
import { Language, LanguageMarshaller } from 'app/constants/enums/Language';
import { Timezone, TimezoneMarshaller } from 'app/constants/schemas/Timezone';
import { TranslationsForEntity, ALL_TRANSLATIONS } from 'app/constants/schemas/translations';
import { IBrandDomain, BrandDomain } from 'app/constants/schemas/BrandDomain';
import { IOrganization, Organization } from 'app/constants/schemas/Organization';
import { IBrandExternalSite, BrandExternalSite } from 'app/constants/schemas/BrandExternalSite';
import { DateMarshaller } from './Date';
import { BrandSiteFeature, IBrandSiteFeature } from './SiteFeatures/BrandSiteFeature';

export interface IBrand extends IEntity {
  attributes: {
    id: EntityStringId;
    /**
     * A URL to the user dashboard page for this Brand.
     */
    dashboardUrl: string | null;
    /**
     * A URL to the landing page for this Brand. Is used as the fallback link on things like
     * logos or User specific redirects.
     */
    landingPageUrl: string | null;
    /**
     * A link to the terms of service for this brand.
     */
    termsOfServiceUrl: string | null;
    /**
     * A link to this brand's privacy policy.
     */
    privacyPolicyUrl: string | null;
    /**
     * A link to an RTC guide.
     */
    rtcGuideUrl: string | null;
    /**
     * A link to a lesson booking page.
     */
    bookingUrl: string | null;
    /**
     * Specifies which language to use on the site when one can not be inferred by other means.
     */
    defaultLanguage: Language;
    /**
     * A list of hreflang strings to consider this brand canonical for.
     *
     * For example, `engoo.es` is considered canoncial for `es` and `es-ES` but `engoo.mx` might have `es-MX` specified.
     * Whatever is set, an organization must ensure that each hreflang is only configured once across all brands.
     */
    canonicalLanguages: string[];
    /**
     * A flag to indicate that this Brand should be used as the default brand when no language is known.
     *
     * For example, in the DMM organization we want Engoo Global to be the absolute default if a persons language
     * preference is unknown.
     */
    canonicalLanguageFallback: boolean;
    /**
     * Timezone considered default for this brand.
     *
     * For example, DMM Eikaiwa might consider `ASIA/TOKYO` as the default timezone.
     */
    fallbackTimezone: Timezone;
    /**
     * Flag indicating that this brand is the DMM brand
     * @deprecated -- This should only be used in situations where it is not possible to implement a more generally
     * white labeled feature. Basing site customizations on this flag and the `Organization` `firstParty` flag is
     * considered brittle.
     */
    firstPartyDmm: boolean;
    /**
     * Email to use for contacting this Brand's customer support.
     */
    analyticsIds: string[];
    /**
     * The date when this Brand was created.
     */
    createdAt: Date;
    /**
     * The date when this Brand was most recently updated.
     */
    updatedAt: Date;
  };
  associations: {
    /**
     * The organizatino that this brand belongs to.
     */
    organization: IOrganization;
    /**
     * A list of domains that should redirect to this brand.
     * NOTE: At least one must have the `default` flag set and the list may not be empty.
     */
    domains: IBrandDomain[];
    /**
     * A list of external sites associated with this brand. These values are used to determine how to create links
     * offsite. Values in this list will be treated similarly to brand domains, although they are different because
     * they are not directly controlled by this site. For example `admin.engoo.com` would be considered a brand
     * external site because it is not directly tied to any individual brand domain and is hosted separately, but
     * links to that domain should have branch server calculations etc.
     */
    externalSites: IBrandExternalSite[];
    /**
     * A description of this Brand (used primarily in the page meta data)
     */
    description: IText | null;
    /**
     * The logo to show in the header for this brand.
     */
    logo: IBrandLogo | null;
    /**
     * The logo to show in the footer for this brand.
     */
    footerLogo: IBrandLogo | null;
    /**
     * A square version of this brand's logo.
     */
    squareLogo: IBrandLogo | null;
    /**
     * The collection of lesson curricula available on this Brand.
     */
    curricula: ICurriculum[];
    /**
     * The name of this brand.
     */
    name: IText;
    /**
     * A shortend version of this brands name.
     *
     * For example if the brand was called `Engoo Global` the short name might be `Engoo`.
     */
    shortName: IText | null;
    siteFeatures: IBrandSiteFeature[];
  };
}

export type Brand = Entity<IBrand>;
// defineClass is a class factory, normally classes create a runtime variable and identifier in the type namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Brand: IEntityType<IBrand> = vmClient.defineClass({
  typeName: 'Brand',
  version: 5,
  persisted: true,
  lockable: true,
  attributes: {
    analyticsIds: {
      type: 'string',
      notNull: true,
      collection: true,
    },
    dashboardUrl: 'string',
    landingPageUrl: 'string',
    termsOfServiceUrl: 'string',
    privacyPolicyUrl: 'string',
    rtcGuideUrl: 'string',
    bookingUrl: 'string',
    defaultLanguage: {
      type: Language,
      notNull: true,
      marshaller: LanguageMarshaller,
    },
    canonicalLanguages: {
      type: 'string',
      collection: true,
      notNull: true,
    },
    canonicalLanguageFallback: {
      type: 'boolean',
      notNull: true,
    },
    fallbackTimezone: {
      type: Timezone,
      marshaller: TimezoneMarshaller,
      notNull: true,
    },
    firstPartyDmm: {
      type: 'boolean',
      notNull: true,
    },
    createdAt: {
      type: Date,
      marshaller: DateMarshaller,
      notNull: true,
    },
    updatedAt: {
      type: Date,
      marshaller: DateMarshaller,
      notNull: true,
    },
  },
  associations: {
    description: Text,
    name: {
      type: Text,
      notNull: true,
    },
    organization: {
      type: Organization,
      notNull: true,
      shared: true,
    },
    domains: {
      type: BrandDomain,
      collection: true,
      notNull: true,
    },
    externalSites: {
      type: BrandExternalSite,
      collection: true,
      notNull: true,
    },
    shortName: Text,
    logo: BrandLogo,
    footerLogo: BrandLogo,
    squareLogo: BrandLogo,
    curricula: {
      type: Curriculum,
      collection: true,
      notNull: true,
    },
    siteFeatures: {
      type: BrandSiteFeature,
      collection: true,
      notNull: true,
    },
  },
});

export const BrandTranslations: Partial<TranslationsForEntity<IBrand>> = {
  analyticsIds: {
    getLabel: (t) => t('entity-fields.Brand.analyticsIds.label'),
  },
  id: {
    getLabel: (t) => t('entity-fields.Brand.id.label'),
  },
  name: {
    getLabel: (t) => t('entity-fields.Brand.name.label'),
  },
  shortName: {
    getLabel: (t) => t('entity-fields.Brand.shortName.label'),
  },
  description: {
    getLabel: (t) => t('entity-fields.Brand.description.label'),
  },
  dashboardUrl: {
    getLabel: (t) => t('entity-fields.Brand.dashboardUrl.label'),
    getDescription: (t) => t('entity-fields.Brand.dashboardUrl.description'),
  },
  landingPageUrl: {
    getLabel: (t) => t('entity-fields.Brand.landingPageUrl.label'),
    getDescription: (t) => t('entity-fields.Brand.landingPageUrl.description'),
  },
  termsOfServiceUrl: {
    getLabel: (t) => t('entity-fields.Brand.termsOfServiceUrl.label'),
    getDescription: (t) => t('entity-fields.Brand.termsOfServiceUrl.description'),
  },
  privacyPolicyUrl: {
    getLabel: (t) => t('entity-fields.Brand.privacyPolicyUrl.label'),
    getDescription: (t) => t('entity-fields.Brand.privacyPolicyUrl.description'),
  },
  bookingUrl: {
    getLabel: (t) => t('entity-fields.Brand.bookingUrl.label'),
    getDescription: (t) => t('entity-fields.Brand.bookingUrl.description'),
  },
  rtcGuideUrl: {
    getLabel: (t) => t('entity-fields.Brand.rtcGuideUrl.label'),
    getDescription: (t) => t('entity-fields.Brand.rtcGuideUrl.description'),
  },
  domains: {
    getLabel: (t) => t('entity-fields.Brand.domains.label'),
  },
  externalSites: {
    getLabel: (t) => t('entity-fields.Brand.externalSites.label'),
    getDescription: (t) => t('entity-fields.Brand.externalSites.description'),
  },
  fallbackTimezone: {
    getLabel: (t) => t('entity-fields.Brand.fallbackTimezone.label'),
  },
  firstPartyDmm: {
    getLabel: () => '',
    getInlineLabel: (t) => t('entity-fields.Brand.firstPartyDmm.inline-label'),
    getDescription: (t) => t('entity-fields.Brand.firstPartyDmm.description'),
  },
  defaultLanguage: {
    getLabel: (t) => t('entity-fields.Brand.defaultLanguage.label'),
  },
  canonicalLanguages: {
    getLabel: (t) => t('entity-fields.Brand.canonicalLanguages.label'),
  },
  canonicalLanguageFallback: {
    getLabel: (t) => t('entity-fields.Brand.canonicalLanguageFallback.label'),
    getInlineLabel: (t) => t('entity-fields.Brand.canonicalLanguageFallback.inline-label'),
    getDescription: (t) => t('entity-fields.Brand.canonicalLanguageFallback.description'),
  },
  logo: {
    getLabel: (t) => t('entity-fields.Brand.logo.label'),
  },
  footerLogo: {
    getLabel: (t) => t('entity-fields.Brand.footerLogo.label'),
    getDescription: (t) => t('entity-fields.Brand.footerLogo.description'),
  },
  squareLogo: {
    getLabel: (t) => t('entity-fields.Brand.squareLogo.label'),
    getDescription: (t) => t('entity-fields.Brand.squareLogo.description'),
  },
  curricula: {
    getLabel: (t) => t('entity-fields.Brand.curricula.label'),
  },
  siteFeatures: {
    getLabel: (t) => t('entity-fields.Brand.siteFeatures.label'),
  },
};

ALL_TRANSLATIONS.set(Brand, BrandTranslations);
